import { Controller } from "@hotwired/stimulus";
import { priceBlockHtml, bestVariant } from "../flash_price";
import { formatTND } from "../lib/currency"

export default class extends Controller {
  static targets = ["pageTitle", "pageSlogan", "perfumesGrid", "pagination", "brandList", "brandSearch", "activeFilters", "sidebar", "backdrop", "filterCountBadge", "resultsCount"];

  allRawPerfumes = [];
  filteredAndSortedPerfumes = [];
  currentPage = 1;
  perfumesPerPage = 12;

  currentGenderFilter = 'all_genders';
  currentClassFilter = 'all_classes';
  currentBrandFilter = 'all_brands';
  currentPriceRange = 'all_prices';
  currentAvailability = 'all';
  currentPriceSort = 'none';

  priceRanges = {
    all_prices: { label: 'Tous les prix', test: () => true },
    under_50: { label: '< 50 TND', test: (p) => p < 50 },
    '50_100': { label: '50–100 TND', test: (p) => p >= 50 && p < 100 },
    '100_200': { label: '100–200 TND', test: (p) => p >= 100 && p < 200 },
    over_200: { label: '200+ TND', test: (p) => p >= 200 },
  };

  classLabels = {
    designer: 'Designer',
    niche: 'Niche',
    collection_privee: 'Collection Privée',
  };

  connect() {
    this.fetchPerfumes();
  }

  async fetchPerfumes() {
    const apiUrl = `/api/v1/parfums`;

    try {
      const response = await fetch(apiUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      this.allRawPerfumes = data;

      this.renderBrandList();
      this.renderActiveFiltersBar();
      this.updateFilterCountBadge();
      this.applyFiltersAndSortAndRender(false);
    } catch (error) {
      console.error("Error fetching perfumes:", error);
      this.perfumesGridTarget.innerHTML = "<p class='error-message'>Impossible de charger les parfums. Veuillez réessayer plus tard.</p>";
      this.pageTitleTarget.textContent = "Erreur de chargement";
      this.pageSloganTarget.textContent = "Une erreur est survenue lors du chargement des parfums.";
    }
  }

  renderBrandList() {
    if (!this.hasBrandListTarget) return;
    this.allBrands = [...new Set(this.allRawPerfumes.map((p) => p.brand && p.brand.name).filter(Boolean))].sort((a, b) => a.localeCompare(b, "fr"));
    this.drawBrandList(this.allBrands);
  }

  drawBrandList(brands) {
    if (brands.length === 0) {
      this.brandListTarget.innerHTML = `<p class="filter-brand-empty">Aucune marque ne correspond.</p>`;
      return;
    }
    this.brandListTarget.innerHTML = brands.map((b) => `
      <button type="button" class="filter-brand-row ${this.currentBrandFilter === b ? 'is-active' : ''}" data-filter-type="brand" data-filter-value="${b}" data-action="click->all-perfumes#setFilter">
        <span class="filter-brand-row-check"></span>${b}
      </button>
    `).join('');
  }

  searchBrands(event) {
    const query = event.target.value.trim().toLowerCase();
    const matches = query ? this.allBrands.filter((b) => b.toLowerCase().includes(query)) : this.allBrands;
    this.drawBrandList(matches);
  }

  toggleFilterPanel() {
    if (!this.hasSidebarTarget) return;
    this.sidebarTarget.classList.toggle('is-open');
    if (this.hasBackdropTarget) this.backdropTarget.classList.toggle('is-open');
  }

  updateFilterCountBadge() {
    if (!this.hasFilterCountBadgeTarget) return;
    let count = 0;
    if (this.currentGenderFilter !== 'all_genders') count++;
    if (this.currentClassFilter !== 'all_classes') count++;
    if (this.currentBrandFilter !== 'all_brands') count++;
    if (this.currentPriceRange !== 'all_prices') count++;
    if (this.currentAvailability !== 'all') count++;
    this.filterCountBadgeTarget.textContent = count;
    this.filterCountBadgeTarget.hidden = count === 0;
  }

  setFilter(event) {
    const { filterType, filterValue } = event.currentTarget.dataset;
    if (filterType === 'gender') this.currentGenderFilter = filterValue;
    else if (filterType === 'class') this.currentClassFilter = filterValue;
    else if (filterType === 'brand') this.currentBrandFilter = filterValue;
    else if (filterType === 'price') this.currentPriceRange = filterValue;
    else if (filterType === 'availability') this.currentAvailability = filterValue;
    else if (filterType === 'sort') this.currentPriceSort = filterValue;
    this.syncPillStates();
    this.applyFiltersAndSortAndRender();
  }

  removeFilter(event) {
    const { filterType } = event.currentTarget.dataset;
    if (filterType === 'gender') this.currentGenderFilter = 'all_genders';
    else if (filterType === 'class') this.currentClassFilter = 'all_classes';
    else if (filterType === 'brand') this.currentBrandFilter = 'all_brands';
    else if (filterType === 'price') this.currentPriceRange = 'all_prices';
    else if (filterType === 'availability') this.currentAvailability = 'all';
    this.syncPillStates();
    this.applyFiltersAndSortAndRender();
  }

  clearFilters() {
    this.currentGenderFilter = 'all_genders';
    this.currentClassFilter = 'all_classes';
    this.currentBrandFilter = 'all_brands';
    this.currentPriceRange = 'all_prices';
    this.currentAvailability = 'all';
    this.syncPillStates();
    this.applyFiltersAndSortAndRender();
  }

  syncPillStates() {
    this.element.querySelectorAll('.filter-pill, .shop-sidebar-option').forEach((btn) => {
      const { filterType, filterValue } = btn.dataset;
      const current = {
        gender: this.currentGenderFilter,
        class: this.currentClassFilter,
        brand: this.currentBrandFilter,
        price: this.currentPriceRange,
        availability: this.currentAvailability,
        sort: this.currentPriceSort,
      }[filterType];
      btn.classList.toggle('is-active', current === filterValue);
    });
    this.element.querySelectorAll('.filter-brand-row').forEach((btn) => {
      btn.classList.toggle('is-active', btn.dataset.filterValue === this.currentBrandFilter);
    });
    this.renderActiveFiltersBar();
    this.updateFilterCountBadge();
  }

  renderActiveFiltersBar() {
    if (!this.hasActiveFiltersTarget) return;
    const chips = [];
    if (this.currentGenderFilter !== 'all_genders') chips.push({ type: 'gender', label: this.capitalizeFirstLetter(this.currentGenderFilter) });
    if (this.currentClassFilter !== 'all_classes') chips.push({ type: 'class', label: this.classLabels[this.currentClassFilter] || this.currentClassFilter });
    if (this.currentBrandFilter !== 'all_brands') chips.push({ type: 'brand', label: this.currentBrandFilter });
    if (this.currentPriceRange !== 'all_prices') chips.push({ type: 'price', label: this.priceRanges[this.currentPriceRange].label });
    if (this.currentAvailability !== 'all') chips.push({ type: 'availability', label: 'Disponible uniquement' });

    if (chips.length === 0) {
      this.activeFiltersTarget.innerHTML = '';
      this.activeFiltersTarget.hidden = true;
      return;
    }

    this.activeFiltersTarget.hidden = false;
    this.activeFiltersTarget.innerHTML = chips.map((c) => `
      <button type="button" class="active-filter-chip" data-action="click->all-perfumes#removeFilter" data-filter-type="${c.type}">
        ${c.label}<span class="active-filter-chip-x">&times;</span>
      </button>
    `).join('') + `<button type="button" class="active-filter-clear" data-action="click->all-perfumes#clearFilters">Tout effacer</button>`;
  }

  applyFiltersAndSortAndRender(resetPage = true) {
    let tempPerfumes = [...this.allRawPerfumes];

    // 1. Apply Gender filter
    if (this.currentGenderFilter !== 'all_genders') {
      tempPerfumes = tempPerfumes.filter(perfume => {
        const perfumeCategoryLower = perfume.category ? perfume.category.toLowerCase() : '';
        return perfumeCategoryLower === this.currentGenderFilter;
      });
    }

    // 2. Apply Class filter
    if (this.currentClassFilter !== 'all_classes') {
      tempPerfumes = tempPerfumes.filter(perfume => {
        const perfumeFragranceClassLower = perfume.fragrance_class ? perfume.fragrance_class.toLowerCase() : '';
        return perfumeFragranceClassLower === this.currentClassFilter;
      });
    }

    // 3. Apply Brand filter
    if (this.currentBrandFilter !== 'all_brands') {
      tempPerfumes = tempPerfumes.filter((perfume) => perfume.brand && perfume.brand.name === this.currentBrandFilter);
    }

    // 4. Apply Price range filter
    if (this.currentPriceRange !== 'all_prices') {
      const range = this.priceRanges[this.currentPriceRange];
      tempPerfumes = tempPerfumes.filter((perfume) => range.test(this.getMinPrice(perfume)));
    }

    // 5. Apply Availability filter
    if (this.currentAvailability === 'available') {
      tempPerfumes = tempPerfumes.filter((perfume) => perfume.disponible);
    }

    // 6. Apply price sort
    if (this.currentPriceSort === 'asc') {
      tempPerfumes.sort((a, b) => this.getMinPrice(a) - this.getMinPrice(b));
    } else if (this.currentPriceSort === 'desc') {
      tempPerfumes.sort((a, b) => this.getMinPrice(b) - this.getMinPrice(a));
    }

    this.filteredAndSortedPerfumes = tempPerfumes;

    if (resetPage) {
      this.currentPage = 1;
    } else {
      const totalPages = Math.max(1, Math.ceil(this.filteredAndSortedPerfumes.length / this.perfumesPerPage));
      this.currentPage = this.restoreSavedPage(totalPages);
    }

    this.updateResultsCount();

    if (this.filteredAndSortedPerfumes.length === 0) {
      this.perfumesGridTarget.innerHTML = "<p class='no-perfumes-message'>Aucun parfum trouvé pour cette sélection pour le moment.</p>";
      this.paginationTarget.innerHTML = '';
      return;
    }

    this.renderPerfumes();
    this.renderPagination();
  }

  updateResultsCount() {
    if (!this.hasResultsCountTarget) return;
    const count = this.filteredAndSortedPerfumes.length;
    this.resultsCountTarget.textContent = `${count} parfum${count !== 1 ? 's' : ''}`;
  }

  renderPerfumes() {
    const startIndex = (this.currentPage - 1) * this.perfumesPerPage;
    const endIndex = startIndex + this.perfumesPerPage;
    const perfumesToDisplay = this.filteredAndSortedPerfumes.slice(startIndex, endIndex);

    const perfumeHtml = perfumesToDisplay.map(perfume => {
      const imageUrl = perfume.image_url || "/placeholder.svg?height=250&width=250&text=Parfum";
      const availabilityStatus = perfume.disponible ? 'Disponible' : 'Rupture de stock';
      const availabilityClass = perfume.disponible ? 'perfume-availability-badge--available' : 'perfume-availability-badge--unavailable';
      
      // Variante au meilleur prix actuel (vente flash incluse)
      const bestV = bestVariant(perfume);
      const defaultSize = bestV ? bestV.size : '10ml';
      const defaultPrice = bestV ? (bestV.promo_price ?? bestV.price) : this.getMinPrice(perfume);
      const defaultMaxQuantity = (bestV && bestV.promo_price != null) ? (bestV.promo_remaining ?? "") : "";

      return `
        <div class="perfume-card">
          <a href="/vitrine/parfums/${perfume.slug || perfume.id}" class="perfume-card-link">
            <div class="perfume-image-wrapper">
              <img src="${imageUrl}" alt="${perfume.name}" class="perfume-image">
              <div class="image-overlay"></div>
            </div>
            <div class="perfume-content">
              <h3 class="perfume-name">${perfume.name}</h3>
              <div class="perfume-card-header-row">
                <p class="perfume-brand">${perfume.brand ? perfume.brand.name : 'Inconnue'}</p>
                <span class="perfume-availability-badge ${availabilityClass}">${availabilityStatus}</span>
              </div>
              <div class="perfume-meta">
                <span class="perfume-category">${perfume.category ? this.capitalizeFirstLetter(perfume.category) : ''}</span>
                <span class="perfume-class">${perfume.fragrance_class ? this.capitalizeFirstLetter(perfume.fragrance_class) : ''}</span>
              </div>
              <div class="perfume-price-row">
                ${priceBlockHtml(perfume)}
                <span class="perfume-volume">${defaultSize}</span>
              </div>
              <div class="perfume-card-actions">
                <button
                  class="perfume-card-add-to-cart-btn"
                  data-action="click->cart#addToCart"
                  data-parfum-id="${perfume.id}"
                  data-parfum-name="${perfume.name}"
                  data-parfum-price="${defaultPrice}"
                  data-parfum-max-quantity="${defaultMaxQuantity}"
                  data-parfum-size="${defaultSize}"
                  data-parfum-image-url="${imageUrl}"
                  ${!perfume.disponible ? 'disabled' : ''}
                >
                  Ajouter au panier
                </button>
              </div>
            </div>
          </a>
        </div>
      `;
    }).join('');

    this.perfumesGridTarget.innerHTML = perfumeHtml;
  }

  renderPagination() {
    const totalPages = Math.ceil(this.filteredAndSortedPerfumes.length / this.perfumesPerPage);
    if (totalPages <= 1) {
      this.paginationTarget.innerHTML = '';
      return;
    }

    let paginationHtml = '<ul class="pagination">';
    paginationHtml += `
      <li class="page-item ${this.currentPage === 1 ? 'disabled' : ''}">
        <a href="#" data-action="click->all-perfumes#previousPage" class="page-link">«</a>
      </li>
    `;
    for (const page of this.getPageNumbers(this.currentPage, totalPages)) {
      if (page === '...') {
        paginationHtml += `
          <li class="page-item disabled page-item--ellipsis">
            <span class="page-link">&hellip;</span>
          </li>
        `;
      } else {
        paginationHtml += `
          <li class="page-item ${this.currentPage === page ? 'current' : ''}">
            <a href="#" data-action="click->all-perfumes#goToPage" data-page="${page}" class="page-link">${page}</a>
        </li>
        `;
      }
    }
    paginationHtml += `
      <li class="page-item ${this.currentPage === totalPages ? 'disabled' : ''}">
        <a href="#" data-action="click->all-perfumes#nextPage" class="page-link">»</a>
      </li>
    `;
    paginationHtml += '</ul>';
    this.paginationTarget.innerHTML = paginationHtml;
  }

  previousPage(event) {
    event.preventDefault();
    if (this.currentPage > 1) {
      this.currentPage--;
      this.savePage();
      this.renderPerfumes();
      this.renderPagination();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  nextPage(event) {
    event.preventDefault();
    const totalPages = Math.ceil(this.filteredAndSortedPerfumes.length / this.perfumesPerPage);
    if (this.currentPage < totalPages) {
      this.currentPage++;
      this.savePage();
      this.renderPerfumes();
      this.renderPagination();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  goToPage(event) {
    event.preventDefault();
    const page = parseInt(event.currentTarget.dataset.page);
    if (page && page !== this.currentPage) {
      this.currentPage = page;
      this.savePage();
      this.renderPerfumes();
      this.renderPagination();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  // Construit la liste des numéros de page à afficher, avec des "..." pour les écarts
  getPageNumbers(current, total) {
    const pages = [];
    for (let i = 1; i <= total; i++) {
      if (i === 1 || i === total || (i >= current - 1 && i <= current + 1)) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== '...') {
        pages.push('...');
      }
    }
    return pages;
  }

  getPageStorageKey() {
    return `pagination:${window.location.pathname}${window.location.search}`;
  }

  restoreSavedPage(totalPages) {
    try {
      const saved = parseInt(sessionStorage.getItem(this.getPageStorageKey()), 10);
      if (saved >= 1 && saved <= totalPages) {
        return saved;
      }
    } catch (e) {}
    return 1;
  }

  savePage() {
    try {
      sessionStorage.setItem(this.getPageStorageKey(), String(this.currentPage));
    } catch (e) {}
  }

  // NOUVELLE MÉTHODE: Obtenir le prix minimum des variantes
  getMinPrice(perfume) {
    if (!perfume.variants || perfume.variants.length === 0) {
      return 0;
    }
    return Math.min(...perfume.variants.map(v => parseFloat(v.price)));
  }

  formatCurrency(amount) {
    return formatTND(amount)
  }

  capitalizeFirstLetter(string) {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
  }
}